import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import useAxiosAuth from '../hooks/useAxiosAuth';
import baseURL from '../utils/baseURL';

// MOC (Mitra One Care) account linking from the desk — Add patient, the
// appointment form's "New patient" mode, and the per-row link icon.
//
// One global patient account; a CONNECTED hospital LINKS to it instead of
// creating a second account. The desk asks the server "does this email/phone
// already exist HERE, or in MOC?" and, for a MOC hit, raises a link request
// the patient consents to by Email OTP, Mobile OTP, or Approval in their app.
// Contract: /api/moc-link/* (see the backend's mocLinkController).

const serializeError = (error) => {
    const data = error?.response?.data;
    if (data && typeof data === 'object') {
        return {
            code: data.code || null,
            message: data.message || null,
            status: error.response?.status || null,
            // 409 REQUEST_PENDING hands the live request back so the UI can
            // jump straight to its code step instead of dead-ending.
            linkRequest: data.linkRequest || null,
        };
    }
    return { code: null, message: error?.message || 'Request failed', status: null, linkRequest: null };
};

const digitsOf = (raw) => String(raw ?? '').replace(/[^0-9]/g, '');

// Which case a desk lookup landed on — null means "free, create as today".
//   'inTenant'     → already a patient record HERE (case A) — the desk forms
//                    apply it inline instead of opening the popup
//   'doctorConsent'→ THE OPEN PLATFORM ONLY. A record exists here, but it is
//                    not this doctor's patient. Tenant #1 is not a hospital:
//                    its doctors are independent and share one patients row per
//                    person, so a row a colleague created is not a relationship
//                    this doctor may assume — the person is asked first, by the
//                    same three channels (POST /moc-link/requests kind:'doctor').
//   'pending'      → a link request for this account is already waiting
//   'moc'          → has a Mitra One Care account elsewhere → link (case B)
//
// ORDER MATTERS. 'doctorConsent' is checked before 'inTenant', because on the
// platform the record existing is exactly what must NOT end the story. It needs
// three facts the server now sends: isOpenPlatform, inTenant.linkedToDoctor
// (false, not null — null means the caller never named a doctor, and then there
// is nothing to ask about) and inTenant.mocAccountId, which is what the consent
// is raised against. Missing any of them, this falls through to 'inTenant' —
// i.e. an older server behaves exactly as it does today.
export const classifyMocLookup = (r) => {
    if (!r) return null;
    if (r.isOpenPlatform && r.inTenant && r.inTenant.linkedToDoctor === false && r.inTenant.mocAccountId) {
        return 'doctorConsent';
    }
    if (r.inTenant) return 'inTenant';
    if (r.pending) return 'pending';
    if (Array.isArray(r.moc) && r.moc.length) return 'moc';
    return null;
};

// POST /moc-link/lookup { email?, phone?, doctorId? }
// → { tenantConnected, inTenant, moc[], pending }
export const lookupMocLink = createAsyncThunk(
    'mocLink/lookup',
    async ({ email, phone, doctorId }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const body = {};
            if (email && String(email).trim()) body.email = String(email).trim();
            if (phone && digitsOf(phone)) body.phone = digitsOf(phone);
            // WHOSE patient list the answer should be about — the doctor the
            // form is booking for, which is not always the caller. Omitted when
            // there is no doctor in play; the server then leaves
            // inTenant.linkedToDoctor null and the caller says nothing about
            // ownership rather than guessing.
            if (doctorId && String(doctorId).trim()) body.doctorId = String(doctorId).trim();
            const res = await axiosInstance.post(`${baseURL}/moc-link/lookup`, body);
            return res.data;
        } catch (error) {
            return rejectWithValue(serializeError(error));
        }
    }
);

// POST /moc-link/requests { mocAccountId, channel, patientId? | draftPatient? }
// → { linkRequest }, except channel 'desk' — see below.
export const createMocLinkRequest = createAsyncThunk(
    'mocLink/createRequest',
    async ({ mocAccountId, channel, patientId, draftPatient, kind, doctorId, attestReason }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const body = { mocAccountId, channel };
            // DESK-WITNESSED consent (owner, 22 Sep 2026): the patient is
            // standing at the desk, so the staff member attests to it in
            // writing instead of sending a code. The reason is the record.
            if (channel === 'desk') body.attestReason = String(attestReason || '').trim();
            // kind 'doctor' asks "may THIS DOCTOR take you on?" and carries no
            // draft — the record already exists; only the doctor_patient row is
            // being consented to. Omitted entirely for the original kind so an
            // older server, which knows nothing of it, is unaffected.
            if (kind && kind !== 'link') {
                body.kind = kind;
                if (doctorId) body.doctorId = doctorId;
                if (patientId) body.patientId = patientId;
            } else if (patientId) body.patientId = patientId;
            else body.draftPatient = draftPatient || {};
            const res = await axiosInstance.post(`${baseURL}/moc-link/requests`, body);
            // A desk attestation is SETTLED by the server in the same call, so it
            // answers { request, status:'confirmed', link, patient } rather than
            // { linkRequest }. Normalise to the request row either way and carry
            // the settled link/patient alongside, so the caller finishes without
            // an OTP or an approval step.
            if (res.data?.request) {
                return {
                    ...res.data.request,
                    settled: res.data.status === 'confirmed',
                    link: res.data.link || null,
                    patient: res.data.patient || null,
                };
            }
            return res.data.linkRequest;
        } catch (error) {
            return rejectWithValue(serializeError(error));
        }
    }
);

// POST /moc-link/requests/:id/resend → { contact, expiresAt, delivery }
export const resendMocLinkOtp = createAsyncThunk(
    'mocLink/resendOtp',
    async ({ linkRequestId }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const res = await axiosInstance.post(`${baseURL}/moc-link/requests/${linkRequestId}/resend`, {});
            return res.data;
        } catch (error) {
            return rejectWithValue(serializeError(error));
        }
    }
);

// POST /moc-link/requests/:id/confirm { otpCode } → { link, patient }
export const confirmMocLinkRequest = createAsyncThunk(
    'mocLink/confirmRequest',
    async ({ linkRequestId, otpCode }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const res = await axiosInstance.post(`${baseURL}/moc-link/requests/${linkRequestId}/confirm`, { otpCode: String(otpCode).trim() });
            return { linkRequestId, ...res.data };
        } catch (error) {
            return rejectWithValue(serializeError(error));
        }
    }
);

// POST /moc-link/requests/:id/cancel → { linkRequest: { …status:'declined' } }
export const cancelMocLinkRequest = createAsyncThunk(
    'mocLink/cancelRequest',
    async ({ linkRequestId }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const res = await axiosInstance.post(`${baseURL}/moc-link/requests/${linkRequestId}/cancel`, {});
            return { linkRequestId, linkRequest: res.data?.linkRequest || null };
        } catch (error) {
            return rejectWithValue(serializeError(error));
        }
    }
);

// GET /moc-link/requests?status=pending|confirmed|declined|expired&mine=1
// → { count, requests[] }
// countOnly: a badge refresh — only pendingCount is updated; the panel's list
// (requests / requestsStatus / loading / error) is untouched, so a refresh
// fired while the panel shows another tab cannot overwrite that tab's rows.
export const listMocLinkRequests = createAsyncThunk(
    'mocLink/listRequests',
    async ({ status = 'pending', mine = false, countOnly = false } = {}, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const params = new URLSearchParams();
            if (status) params.set('status', status);
            if (mine) params.set('mine', '1');
            const res = await axiosInstance.get(`${baseURL}/moc-link/requests?${params.toString()}`);
            return { status, mine, countOnly, count: res.data?.count ?? (res.data?.requests || []).length, requests: res.data?.requests || [] };
        } catch (error) {
            return rejectWithValue(serializeError(error));
        }
    }
);

const mocLinkSlice = createSlice({
    name: 'mocLink',
    initialState: {
        // The list the Pending-links panel is showing (one status at a time).
        requests: [],
        requestsCount: 0,
        requestsStatus: 'pending',
        requestsLoading: false,
        requestsError: null,
        // Badge on the Pending-links tab — kept even while another status is listed.
        pendingCount: 0,
        // Socket `moc-link:decided` — pages bump their own refresh keys off this.
        decidedTick: 0,
        lastDecision: null,
    },
    reducers: {
        mocLinkDecided(state, action) {
            state.decidedTick += 1;
            state.lastDecision = action.payload || null;
            const id = action.payload?.linkRequestId;
            if (id && state.requestsStatus === 'pending') {
                const before = state.requests.length;
                state.requests = state.requests.filter((r) => r.linkRequestId !== id);
                if (state.requests.length !== before) {
                    state.requestsCount = Math.max(0, state.requestsCount - 1);
                    state.pendingCount = Math.max(0, state.pendingCount - 1);
                }
            }
        },
    },
    extraReducers: (builder) => {
        const dropPending = (state, linkRequestId) => {
            if (!linkRequestId) return;
            if (state.requestsStatus === 'pending') {
                const before = state.requests.length;
                state.requests = state.requests.filter((r) => r.linkRequestId !== linkRequestId);
                if (state.requests.length !== before) state.requestsCount = Math.max(0, state.requestsCount - 1);
            }
            state.pendingCount = Math.max(0, state.pendingCount - 1);
        };
        builder
            .addCase(listMocLinkRequests.pending, (state, action) => {
                if (action.meta.arg?.countOnly) return;
                state.requestsLoading = true;
                state.requestsError = null;
            })
            .addCase(listMocLinkRequests.fulfilled, (state, action) => {
                const { status, count, requests, countOnly } = action.payload;
                if (status === 'pending') state.pendingCount = count;
                // Only the panel's own call writes the list — a badge refresh
                // must not replace the rows of whichever tab is showing.
                if (countOnly) return;
                state.requestsLoading = false;
                state.requests = requests;
                state.requestsCount = count;
                state.requestsStatus = status;
            })
            .addCase(listMocLinkRequests.rejected, (state, action) => {
                if (action.meta.arg?.countOnly) return;
                state.requestsLoading = false;
                state.requestsError = action.payload || null;
            })
            .addCase(createMocLinkRequest.fulfilled, (state) => {
                // A new pending request exists server-side; the panel re-lists
                // on its own, but the badge should not lag behind.
                state.pendingCount += 1;
            })
            .addCase(confirmMocLinkRequest.fulfilled, (state, action) => {
                dropPending(state, action.payload?.linkRequestId);
            })
            .addCase(cancelMocLinkRequest.fulfilled, (state, action) => {
                dropPending(state, action.payload?.linkRequestId);
            });
    },
});

export const { mocLinkDecided } = mocLinkSlice.actions;
export default mocLinkSlice.reducer;
